// src/lib/coverage/computeCoverage.ts
// ============================================================
// COVERAGE REDUCER — the single source of every coverage number
//
// Replaces the one-dimensional Personalization Strength percentage with a two-dimensional
// model: how MANY measures are informed (breadth), and how WELL each one is informed (tier).
//
// ─────────────────────────────────────────────────────────────────────────────
// THE HARD RULE — no component may hold a number that is derivable here.
//
// Every count, every ratio, every "N of 38" is produced by reduction over the measure
// array below. Nothing is stored. If a string needs a number in it, the string takes the
// number as a parameter; it does not carry a literal. A literal count in a component is
// how the old surface came to show 93% on one page and 84% on another.
// ─────────────────────────────────────────────────────────────────────────────
//
// PRECEDENCE, strict and total: evidence > survey > profile > default.
// A measure informed by both a document and a survey resolves to `evidence`, and the
// survey is retained on the record in `alsoInformedBy` so the override display can show
// what was superseded. Nothing is discarded, only ranked.
//
// Mock layer only. No backend call, no fetch, no localStorage read. Pure function of its
// argument, so it is safe under the static build regime and trivially testable.
// ============================================================

import {
  CATEGORY_TO_GROUP,
  COVERAGE_CATEGORIES,
  COVERAGE_MEASURE_IDS,
  MEASURE_GROUPS,
  MEASURE_TO_CATEGORY,
  type CoverageCategory,
  type MeasureGroup,
} from '../../data/coverage/measureGroupMap';
import { displayName } from '../../data/coverage/measureDisplayNames';
import {
  SURVEY_BINDING_BY_ID,
  type SurveyModuleId,
} from '../../data/coverage/surveyMeasureBindings';
import { selfReportedScore } from './surveyScoring';
import {
  PROFILE_BINDING_BY_KEY,
  type ProfileFieldKey,
} from '../../data/coverage/profileMeasureBindings';

// ---- types -----------------------------------------------------------------

export type Tier = 'evidence' | 'survey' | 'profile' | 'default';

/** Strict precedence. Higher wins. Used for comparison only, never displayed. */
const TIER_RANK: Record<Tier, number> = { evidence: 3, survey: 2, profile: 1, default: 0 };

/** Every tier, strongest first. Display order for a tier legend. */
export const TIERS: readonly Tier[] = ['evidence', 'survey', 'profile', 'default'] as const;

/** What kind of thing an evidence source is. Becomes the source string prefix. */
export type EvidenceKind = 'Internal document' | 'Structured inventory' | 'External document';

export interface EvidenceSource {
  id: string;
  /** Human label. Rendered verbatim after the kind prefix. */
  label: string;
  /** Defaults to 'Internal document'. */
  kind?: EvidenceKind;
  /** Measures this source evidences. */
  measureIds: number[];
  /**
   * Per-measure scores on the native 1 to 5 scale, where the source produced one.
   * A source may evidence a measure without scoring it.
   */
  scores?: Record<number, number>;
  /**
   * Per-measure supporting statement, e.g. "2 of 9 platforms at real time or near real
   * time". DERIVED from the parsed rows by deriveScores, never authored: it is the half of
   * an override that makes the claim checkable, so an authored one would be worthless.
   */
  bases?: Record<number, string>;
  /**
   * Categories this source structurally covered. Any measure in one of these categories
   * that ends on `default` is marked assumed: the input looked at that category and had
   * nothing to say about this particular measure.
   */
  coversCategories?: CoverageCategory[];
  /** Measures explicitly known to be assumed. Union'd with the coversCategories result. */
  assumedMeasureIds?: number[];
}

export interface CoverageInputs {
  /** Survey modules the client has completed. */
  completedSurveyModules?: SurveyModuleId[];
  /** Profile fields that carry a value. */
  populatedProfileFields?: ProfileFieldKey[];
  /** Documents and workbooks that produced evidence. */
  evidenceSources?: EvidenceSource[];
}

/** A source that informed a measure, at whatever tier. */
export interface InformingSource {
  tier: Tier;
  source: string;
  score: number | null;
  /** Supporting statement derived from the input's own rows. Null when it has none. */
  basis: string | null;
}

export interface MeasureCoverage {
  measureId: number;
  displayName: string;
  category: CoverageCategory;
  /** Reporting group, not a scoring index. See measureGroupMap's header. */
  group: MeasureGroup;
  tier: Tier;
  /** e.g. 'Internal document: Data Asset and Platform Inventory'. Null on default. */
  source: string | null;
  /** True when a structured input covered the category but not this measure. */
  assumed: boolean;
  /**
   * The source that declared it assumed, e.g. 'Internal document: Board Priorities Extract'.
   * Recorded rather than inferred: the copy explaining an assumption has to name what did
   * the looking, and guessing from the category would be wrong the moment a second
   * instrument declares coverage.
   */
  assumedBy: string | null;
  /** Score on the native 1 to 5 scale, when the winning tier produced one. */
  score: number | null;
  /** Supporting statement from the winning input. Null when it has none. */
  basis: string | null;
  /** Lower-precedence inputs, strongest first. Retained for the override display. */
  alsoInformedBy: InformingSource[];
}

export interface CoverageSlice {
  total: number;
  informed: number;
  evidence: number;
}

export interface CoverageAggregates {
  /** Catalog size. Derived from the measure array, never typed. */
  total: number;
  /** Measures at each tier. Sums to `total`. */
  counts: Record<Tier, number>;
  /** Measures on any tier other than default. */
  breadth: number;
  /** Measures a structured input touched the category of but could not inform. */
  assumed: number;
  byGroup: Record<MeasureGroup, CoverageSlice>;
  byCategory: Record<CoverageCategory, CoverageSlice>;
}

export interface CoverageResult {
  measures: MeasureCoverage[];
  aggregates: CoverageAggregates;
}

// ---- reducer ---------------------------------------------------------------

/** Working record before it is frozen into a MeasureCoverage. */
interface Claim {
  tier: Tier;
  source: string;
  score: number | null;
  basis: string | null;
}

function betterClaim(a: Claim | null, b: Claim): Claim {
  if (a === null) return b;
  return TIER_RANK[b.tier] > TIER_RANK[a.tier] ? b : a;
}

/**
 * Resolve every measure in the catalog against the supplied inputs.
 *
 * Pure. Same inputs always give the same result, including array ordering, which is
 * ascending measure id throughout.
 */
export function computeCoverage(inputs: CoverageInputs = {}): CoverageResult {
  const claims = new Map<number, Claim[]>();
  /** measure id -> the source label that declared it assumed. */
  const assumedBySource = new Map<number, string>();

  const claim = (measureId: number, next: Claim) => {
    // Ignore ids outside the catalog rather than inventing a row for them.
    if (MEASURE_TO_CATEGORY[measureId] === undefined) return;
    const list = claims.get(measureId);
    if (list) list.push(next);
    else claims.set(measureId, [next]);
  };

  // --- profile (weakest structured tier) ---
  (inputs.populatedProfileFields ?? []).forEach((key) => {
    const binding = PROFILE_BINDING_BY_KEY[key];
    if (!binding) return;
    binding.measureIds.forEach((id) =>
      claim(id, {
        tier: 'profile',
        source: `Organization profile: ${binding.label}`,
        score: null,
        basis: null,
      }),
    );
  });

  // --- survey ---
  (inputs.completedSurveyModules ?? []).forEach((moduleId) => {
    const binding = SURVEY_BINDING_BY_ID[moduleId];
    if (!binding) return;
    // The module's self-reported value, DERIVED from its own seeded answers. Null for a
    // module whose questions cannot produce one (Strategic Priorities), in which case the
    // measure is still informed and simply has nothing to compare a document against.
    const moduleScore = selfReportedScore(moduleId);
    binding.measureIds.forEach((id) =>
      claim(id, {
        tier: 'survey',
        source: `Survey: ${binding.label}`,
        score: moduleScore,
        basis: null,
      }),
    );
  });

  // --- evidence (strongest) ---
  (inputs.evidenceSources ?? []).forEach((src) => {
    const prefix = src.kind ?? 'Internal document';
    const source = `${prefix}: ${src.label}`;
    src.measureIds.forEach((id) =>
      claim(id, {
        tier: 'evidence',
        source,
        score: src.scores?.[id] ?? null,
        basis: src.bases?.[id] ?? null,
      }),
    );
    (src.assumedMeasureIds ?? []).forEach((id) => assumedBySource.set(id, source));
    // "Covered the category but not this measure" — so only the measures in a covered
    // category that this source did NOT reach. A source that scored a measure has, by
    // definition, not assumed it.
    const reached = new Set(src.measureIds);
    (src.coversCategories ?? []).forEach((category) => {
      COVERAGE_MEASURE_IDS.forEach((id) => {
        if (MEASURE_TO_CATEGORY[id] === category && !reached.has(id)) {
          assumedBySource.set(id, source);
        }
      });
    });
  });

  const measures: MeasureCoverage[] = COVERAGE_MEASURE_IDS.map((measureId) => {
    const category = MEASURE_TO_CATEGORY[measureId];
    const group = CATEGORY_TO_GROUP[category];
    const list = claims.get(measureId) ?? [];

    const winner = list.reduce<Claim | null>(betterClaim, null);
    const tier: Tier = winner?.tier ?? 'default';

    const alsoInformedBy: InformingSource[] = list
      .filter((c) => c !== winner)
      .sort((a, b) => TIER_RANK[b.tier] - TIER_RANK[a.tier])
      .map((c) => ({ tier: c.tier, source: c.source, score: c.score, basis: c.basis }));

    return {
      measureId,
      displayName: displayName(measureId),
      category,
      group,
      tier,
      source: winner?.source ?? null,
      // ASSUMED IS TIER-INDEPENDENT. It records that a structured input looked at this
      // measure's category and had nothing to say about this measure, which stays true
      // however the measure was eventually informed. An earlier build gated this on
      // tier === 'default', which made assumed a strict subset of default and collapsed
      // the real case: the board workbook finds no Market Position priority while a board
      // PDF has already evidenced measure 6. Both facts are worth showing, and the gap
      // list is specified as "on library default OR marked assumed" precisely because the
      // two sets are not nested.
      assumed: assumedBySource.has(measureId),
      assumedBy: assumedBySource.get(measureId) ?? null,
      score: winner?.score ?? null,
      basis: winner?.basis ?? null,
      alsoInformedBy,
    };
  });

  return { measures, aggregates: aggregate(measures) };
}

/** Every total, by reduction over the array. Nothing here is stored anywhere. */
export function aggregate(measures: MeasureCoverage[]): CoverageAggregates {
  const counts: Record<Tier, number> = { evidence: 0, survey: 0, profile: 0, default: 0 };
  const emptySlice = (): CoverageSlice => ({ total: 0, informed: 0, evidence: 0 });

  const byGroup = MEASURE_GROUPS.reduce(
    (acc, g) => {
      acc[g] = emptySlice();
      return acc;
    },
    {} as Record<MeasureGroup, CoverageSlice>,
  );

  const byCategory = COVERAGE_CATEGORIES.reduce(
    (acc, c) => {
      acc[c] = emptySlice();
      return acc;
    },
    {} as Record<CoverageCategory, CoverageSlice>,
  );

  let assumed = 0;

  measures.forEach((m) => {
    counts[m.tier] += 1;
    if (m.assumed) assumed += 1;

    const slices = [byGroup[m.group], byCategory[m.category]];
    slices.forEach((s) => {
      if (!s) return;
      s.total += 1;
      if (m.tier !== 'default') s.informed += 1;
      if (m.tier === 'evidence') s.evidence += 1;
    });
  });

  return {
    total: measures.length,
    counts,
    breadth: measures.length - counts.default,
    assumed,
    byGroup,
    byCategory,
  };
}

/**
 * Format a coverage ratio for display. The STRING takes the numbers; it never holds them.
 * Kept here so no component has to assemble "N of M" from two props and get the order wrong.
 */
export function coverageLabel(informed: number, total: number): string {
  return `${informed} of ${total} measures informed`;
}

// ---- adapter ---------------------------------------------------------------

/** The subset of a derived score this module needs. Kept structural to avoid a cycle. */
export interface ScoreLike {
  measureId: number;
  score: number | null;
  assumed: boolean;
  /** Derived supporting statement. Carried onto the evidence source verbatim. */
  basis?: string;
}

/**
 * Turn a workbook's derived scores into an evidence source.
 *
 * Measures the workbook scored become evidence. Measures it returned assumed for are
 * declared assumed, and `coversCategories` extends that to anything else in the same
 * category the workbook did not reach.
 */
export function evidenceSourceFromScores(
  meta: { id: string; label: string; kind?: EvidenceKind; coversCategories?: CoverageCategory[] },
  derived: ScoreLike[],
): EvidenceSource {
  const scored = derived.filter((d) => d.score !== null);
  return {
    id: meta.id,
    label: meta.label,
    kind: meta.kind ?? 'Structured inventory',
    coversCategories: meta.coversCategories,
    measureIds: scored.map((d) => d.measureId),
    scores: scored.reduce<Record<number, number>>((acc, d) => {
      acc[d.measureId] = d.score as number;
      return acc;
    }, {}),
    bases: scored.reduce<Record<number, string>>((acc, d) => {
      if (d.basis) acc[d.measureId] = d.basis;
      return acc;
    }, {}),
    assumedMeasureIds: derived.filter((d) => d.assumed).map((d) => d.measureId),
  };
}
